/**
 * In-memory undo/redo history for board canvas operations.
 *
 * History is intentionally per-tab and bounded. Operations contain only the
 * state needed to apply their inverse; project/task data stays in the canvas.
 */
import type { BoardItemStyle } from './types';

export interface BoardXY {
  x: number;
  y: number;
}

export interface BoardRect extends BoardXY {
  width: number;
  height: number;
}

export type BoardOp =
  | { type: 'move'; items: { id: string; from: BoardXY; to: BoardXY }[] }
  | { type: 'resize'; id: string; from: BoardRect; to: BoardRect }
  | { type: 'style'; id: string; from: BoardItemStyle; to: BoardItemStyle }
  | { type: 'content'; id: string; from: string; to: string }
  | { type: 'layer'; items: { id: string; from: number; to: number }[] }
  | { type: 'delete'; itemIds: string[] }
  | { type: 'create'; itemIds: string[] };

export interface BoardHistory {
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  push(operation: BoardOp): void;
  undo(): BoardOp | null;
  redo(): BoardOp | null;
  /** Remove an operation undone by an independent toast action. */
  remove(operation: BoardOp): void;
  /** Restore the cursor when an asynchronous application fails. */
  rollbackUndo(operation: BoardOp): void;
  rollbackRedo(operation: BoardOp): void;
  clear(): void;
}

export function createBoardHistory(options: { limit?: number; onChange?: () => void } = {}) {
  const limit = Math.max(1, options.limit ?? 50);
  const past: BoardOp[] = [];
  const future: BoardOp[] = [];

  const changed = () => options.onChange?.();

  const history: BoardHistory = {
    get canUndo() {
      return past.length > 0;
    },
    get canRedo() {
      return future.length > 0;
    },
    push(operation) {
      past.push(operation);
      if (past.length > limit) past.shift();
      future.length = 0;
      changed();
    },
    undo() {
      const operation = past.pop() ?? null;
      if (!operation) return null;
      future.push(operation);
      changed();
      return operation;
    },
    redo() {
      const operation = future.pop() ?? null;
      if (!operation) return null;
      past.push(operation);
      changed();
      return operation;
    },
    remove(operation) {
      const removeFrom = (stack: BoardOp[]) => {
        const index = stack.indexOf(operation);
        if (index >= 0) stack.splice(index, 1);
      };
      removeFrom(past);
      removeFrom(future);
      changed();
    },
    rollbackUndo(operation) {
      if (future[future.length - 1] !== operation) return;
      future.pop();
      past.push(operation);
      changed();
    },
    rollbackRedo(operation) {
      if (past[past.length - 1] !== operation) return;
      past.pop();
      future.push(operation);
      changed();
    },
    clear() {
      past.length = 0;
      future.length = 0;
      changed();
    },
  };

  return history;
}
